import { Button, Typography, Row, Col, Tag } from 'antd';
import { motion } from 'framer-motion';
import {
    ReloadOutlined,
    CloseOutlined,
    UserOutlined,
    BankOutlined,
    PrinterOutlined,
} from '@ant-design/icons';
import { FaTicketAlt } from 'react-icons/fa';
import { IAwardWithWinner } from '../../../Steps/ImportData/useImportData';
import { IRulesGiveaway } from '../../../../../../interfaces';
import { awardImagesUrl, urlHttpGov } from '../../../../../../services/urls';
import { convertMoedaBRDoJSNovo } from '../../../../../../utils/Conversores';
import ImagemLogo from '../../../../../../assets/images/logo-lateral.png';
import useAuth from '../../../../../../hooks/useAuth';

const { Title, Text } = Typography;

interface DrawSummaryProps {
    giveawayTitle?: string;
    cityName?: string;
    attemptNumber?: number;
    awards: IAwardWithWinner[];
    rules?: IRulesGiveaway;
    onRestartDraw: () => void;
    onClose: () => void;
}

export default function DrawSummary({
    giveawayTitle,
    awards,
    rules,
    onRestartDraw,
    onClose,
}: DrawSummaryProps) {
    const { activeCity } = useAuth();

    const cityLogoUrl = activeCity?.logo_county
        ? `${urlHttpGov}/logo_county/${activeCity.logo_county}`
        : activeCity?.logo_light
            ? `${urlHttpGov}/logo_county/${activeCity.logo_light}`
            : null;

    const handlePrint = () => {
        window.print();
    };

    return (
        <div
            style={{
                maxWidth: 1080,
                width: '100%',
                margin: '0 auto',
                padding: '24px 20px',
                display: 'flex',
                flexDirection: 'column',
                gap: 20,
            }}
        >
            {/* Official Report Header Banner (Clean Light) */}
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                style={{
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: 16,
                    padding: '20px 24px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.03)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 16,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <img
                            src={ImagemLogo}
                            alt="Nota Município"
                            style={{ height: 34, objectFit: 'contain' }}
                        />
                        {cityLogoUrl && (
                            <>
                                <div style={{ height: 24, width: 1, background: '#e2e8f0' }} />
                                <img
                                    src={cityLogoUrl}
                                    alt="Prefeitura"
                                    style={{ height: 32, maxWidth: 120, objectFit: 'contain' }}
                                />
                            </>
                        )}
                    </div>

                    <div>
                        <Title
                            level={3}
                            style={{
                                color: '#0f172a',
                                margin: '2px 0 0 0',
                                fontWeight: 800,
                                fontSize: 20,
                            }}
                        >
                            Resultado Oficial dos Ganhadores
                        </Title>
                        <Text style={{ color: '#64748b', fontSize: 13 }}>
                            {giveawayTitle} • Total de {awards.length} prêmios apurados
                        </Text>
                    </div>
                </div>

                {/* Actions (No 'Copiar Ata') */}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }} className="no-print">
                    <Button
                        type="default"
                        icon={<ReloadOutlined />}
                        onClick={onRestartDraw}
                        style={{
                            borderRadius: 8,
                            background: '#ffffff',
                            borderColor: '#cbd5e1',
                            color: '#334155',
                            fontWeight: 600,
                        }}
                    >
                        Assistir Apuração
                    </Button>

                    <Button
                        type="default"
                        icon={<PrinterOutlined />}
                        onClick={handlePrint}
                        style={{
                            borderRadius: 8,
                            background: '#ffffff',
                            borderColor: '#cbd5e1',
                            color: '#334155',
                            fontWeight: 600,
                        }}
                    >
                        Imprimir
                    </Button>
                </div>
            </motion.div>

            {/* Winners Grid */}
            <Row gutter={[16, 16]}>
                {awards.map((award, index) => {
                    const rankNumber = index + 1;
                    const ticketId =
                        award.winner?.winning_ticket_id ??
                        (award as any).winning_ticket_id ??
                        (award as any).winning_ticket_auto_id ??
                        '000000';

                    const winnerName =
                        award.winner?.app_user_name ??
                        (award as any).app_user?.name ??
                        (award as any).app_user_auto?.name ??
                        'Contemplado(a)';

                    const institutionName =
                        award.winner?.social_institution_name ??
                        (award as any).social_institution_name;

                    const rankBadgeClass =
                        rankNumber === 1
                            ? 'rank-badge-1'
                            : rankNumber === 2
                                ? 'rank-badge-2'
                                : rankNumber === 3
                                    ? 'rank-badge-3'
                                    : 'rank-badge-default';

                    return (
                        <Col key={award.id || index} xs={24} sm={12} lg={8}>
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.03, duration: 0.25 }}
                                style={{ height: '100%' }}
                            >
                                <div
                                    className="municipal-card"
                                    style={{
                                        padding: '16px 18px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        gap: 12,
                                        height: '100%',
                                        background: '#ffffff',
                                    }}
                                >
                                    {/* Header: Rank + Prize Details */}
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                        <div
                                            className={rankBadgeClass}
                                            style={{
                                                padding: '3px 8px',
                                                borderRadius: 6,
                                                fontWeight: 800,
                                                fontSize: 12,
                                                textAlign: 'center',
                                                minWidth: 38,
                                                flexShrink: 0,
                                            }}
                                        >
                                            {rankNumber}º
                                        </div>

                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div
                                                style={{
                                                    fontSize: 14,
                                                    fontWeight: 700,
                                                    color: '#0f172a',
                                                    lineHeight: 1.3,
                                                }}
                                            >
                                                {award.description}
                                            </div>
                                        </div>

                                        {award.images && award.images.length > 0 && (
                                            <div
                                                style={{
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: 8,
                                                    background: '#f8fafc',
                                                    border: '1px solid #e2e8f0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    overflow: 'hidden',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <img
                                                    src={`${awardImagesUrl}/${award.images[0].img}`}
                                                    alt={award.description}
                                                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Official Ticket Plate */}
                                    <div
                                        style={{
                                            background: '#f8fafc',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: 8,
                                            padding: '8px 12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <span style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                                                Número do bilhete
                                            </span>
                                            <span
                                                style={{
                                                    fontSize: 16,
                                                    fontWeight: 800,
                                                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                                                    color: '#0f172a',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 6,
                                                }}
                                            >
                                                <FaTicketAlt size={12} color="#2563eb" />
                                                {ticketId}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Winner info */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                                        {rules?.show_winner_name && winnerName && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#1e293b' }}>
                                                <UserOutlined style={{ color: '#2563eb', fontSize: 13 }} />
                                                <span>
                                                    <Text type="secondary" style={{ color: '#64748b' }}>Ganhador:</Text>{' '}
                                                    <strong>{winnerName}</strong>
                                                </span>
                                            </div>
                                        )}

                                        {rules?.show_social_institution_name && institutionName && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', color: '#1e293b' }}>
                                                <BankOutlined style={{ color: '#16a34a', fontSize: 13 }} />
                                                <span>
                                                    <Text type="secondary" style={{ color: '#64748b' }}>Instituição:</Text>{' '}
                                                    <strong>{institutionName}</strong>
                                                </span>
                                                {award.social_institution_value && Number(award.social_institution_value) > 0 && (
                                                    <Tag color="green" style={{ borderRadius: 4, fontWeight: 700, fontSize: 11, margin: 0 }}>
                                                        + R$ {convertMoedaBRDoJSNovo(award.social_institution_value)}
                                                    </Tag>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        </Col>
                    );
                })}
            </Row>

            {/* Bottom Close */}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }} className="no-print">
                <Button
                    type="primary"
                    size="large"
                    icon={<CloseOutlined />}
                    onClick={onClose}
                    style={{
                        height: 46,
                        padding: '0 32px',
                        fontSize: 15,
                        fontWeight: 700,
                        borderRadius: 10,
                        background: '#2563eb',
                        border: 'none',
                        boxShadow: '0 2px 8px rgba(37, 99, 235, 0.25)',
                    }}
                >
                    Fechar Apuração
                </Button>
            </div>
        </div>
    );
}
